const API_URL = `${process.env.REACT_APP_API_URL}/log-interaction`;

const DEBUG_MODE = process.env.NODE_ENV !== 'production'; // Enable debug logging in dev mode

// Only run this in the browser
let SESSION_ID;
if (typeof window !== 'undefined') {
    SESSION_ID = localStorage.getItem('sessionId') || generateSessionId();
    localStorage.setItem('sessionId', SESSION_ID);
}

// Generate a unique session ID
function generateSessionId() {
    return `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

// Function to get device type
const getDeviceType = () => {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent.toLowerCase();

    if (/mobi|android|iphone|ipad/.test(userAgent)) return 'Mobile';
    if (width < 1024) return 'Tablet';
    return 'Desktop';
};

// Fetch offline logs from localStorage before modifying
const getOfflineLogs = () => {
    if (typeof window !== 'undefined') {
        return JSON.parse(localStorage.getItem('offlineLogs')) || [];
    }
    return [];
};

const saveOfflineLogs = (logs) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('offlineLogs', JSON.stringify(logs));
    }
};

// Function to send stored offline logs when back online with retry logic
const sendOfflineLogs = async () => {
    let offlineLogs = getOfflineLogs();
    if (offlineLogs.length === 0) return;

    try {
        const response = await fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                eventType: "offline_logs",
                interactions: offlineLogs, // ✅ Send all logs in one request
            }),
        });

        if (response.ok) {
            console.log(`✅ Sent ${offlineLogs.length} offline logs to MongoDB`);
            if (typeof window !== 'undefined') {
                localStorage.removeItem('offlineLogs'); // ✅ Only clear logs if successful
            }
        } else {
            throw new Error(`Server responded with ${response.status}`);
        }
    } catch (error) {
        console.error('❌ Failed to send offline logs:', error);
    }
};

// Track time spent on elements
const interactionTimers = new Map();
const sessionStart = Date.now();
let idleTime = 0;
let lastActivityTime = Date.now();
let isIdle = false;

// Detect user activity and reset idle timer
const handleActivity = () => {
    if (isIdle) {
        isIdle = false;
    }
    lastActivityTime = Date.now();
};

// Check idle time every 10 seconds
setInterval(() => {
    if (Date.now() - lastActivityTime > 30000) { // 30 seconds of inactivity
        isIdle = true;
        idleTime += 30;
    }
}, 10000);

// MongoDB Batching Setup
let interactionQueue = [];
const BATCH_INTERVAL = 5000; // Save logs every 5 seconds

const saveToDatabase = async () => {
    if (interactionQueue.length > 0) {
        try {
            const batch = interactionQueue.splice(0, 10); // Limit batch to 10 logs
            await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventType: "batch_events", // ✅ Added eventType
                    interactions: batch, // ✅ Wrapped interactions inside an object
                }),
            });
            console.log(`✅ Saved ${batch.length} interactions to MongoDB`);
        } catch (error) {
            console.error('❌ Error saving to MongoDB:', error);
        }
    }
};

// Start batch saving at regular intervals
setInterval(saveToDatabase, BATCH_INTERVAL);

export const startInteractionTimer = (elementId) => {
    interactionTimers.set(elementId, Date.now());
};

export const endInteractionTimer = (elementId, eventType = 'element_interaction') => {
    const startTime = interactionTimers.get(elementId);
    if (!startTime) return;

    const duration = Date.now() - startTime;
    logInteraction(eventType, { elementId, duration });
    interactionTimers.delete(elementId);
};

// Function to log user interactions with consent check and deduplication
export const logInteraction = async (eventType, details) => {
    const userConsent = typeof window !== 'undefined' ? localStorage.getItem('userConsent') : null;

    // Respect "Do Not Track" settings & user consent
    if (navigator.doNotTrack === '1' || userConsent !== 'accepted') {
        if (DEBUG_MODE) {
            console.warn(`⚠ Consent not given or DNT enabled. Skipping log for event: ${eventType}`);
        }
        return;
    }

    try {
        const userDevice = {
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            userAgent: navigator.userAgent,
            language: navigator.language,
            deviceType: getDeviceType(),
            onlineStatus: navigator.onLine ? 'Online' : 'Offline',
            referrer: document.referrer || 'Direct',
            pageURL: window.location.href,
        };

        // Unique Event ID to prevent duplicates
        const logData = {
            eventId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            eventType,
            details,
            device: userDevice,
            sessionId: SESSION_ID,
            timestamp: new Date().toISOString(),
        };

        // If offline, store the log for later
        if (!navigator.onLine) {
            let offlineLogs = getOfflineLogs();
            offlineLogs.push(logData);

            // Limit stored logs to prevent localStorage overflow
            if (offlineLogs.length > 100) {
                offlineLogs.shift(); // Remove oldest log
            }

            saveOfflineLogs(offlineLogs);

            if (DEBUG_MODE) {
                console.warn('⚠ User offline, storing log locally:', logData);
            }
            return;
        }

        // Add logs to the queue for batch processing
        interactionQueue.push(logData);

        if (DEBUG_MODE) {
            console.log(`✅ Logged Interaction: ${eventType}`, logData);
        }
    } catch (error) {
        console.error('❌ Error logging interaction:', error);
    }
};

// ✅ Clear logs if user withdraws consent
function checkUserConsent() {
    if (typeof window !== 'undefined' && localStorage.getItem('userConsent') !== 'accepted') {
        console.log('🛑 User withdrew consent, clearing logs...');
        localStorage.removeItem('offlineLogs');
    }
}

// ✅ Apply CSP Nonce to Inline Scripts (if needed)
function applyNonce() {
    const csp = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (!csp) return;
    const match = csp.content.match(/'nonce-([^']+)'/);
    if (match && match[1]) {
        document.querySelectorAll('script[id]').forEach(script => {
            script.setAttribute('nonce', match[1]);
        });
    }
}

// ✅ Run on page load
window.addEventListener('DOMContentLoaded', () => {
    checkUserConsent();
    applyNonce(); // Apply nonce to inline scripts if needed
});

// Send offline logs when back online
window.addEventListener('online', sendOfflineLogs);

// Log session engagement time on page unload
window.addEventListener('beforeunload', () => {
    const totalTime = Math.round((Date.now() - sessionStart) / 1000);
    const activeTime = totalTime - idleTime;
    if (activeTime > 0) {
        logInteraction('session_time', { duration: activeTime });
    }
});

// Detect user activity
window.addEventListener('mousemove', handleActivity);
window.addEventListener('keydown', handleActivity);
window.addEventListener('scroll', handleActivity);
